import { get, retry } from "@/utils/axios";
import { URL } from "@/utils/url";
import {
  sortByDate,
  sortByAlphabetOrder,
  generateRandomChar,
  getMonthName,
  numberFormat,
  getConditionalStatusWithPartnerStatus,
  getMonthByName,
} from "@/utils/common";

import {
  investmentAppetiteItemType,
  investmentAppetiteType,
  fundCommitmentType,
  FlexibleTableProp,
  instituteProps,
  jsonFilePath,
  partnerStatuses,
  isBrandToSkip,
} from "@/common";

const fs = require("fs");
const maxNumberOfRecords = 5000;
const pageNumber = 1;

const {
  routes: { relatedNews, contactInfo, fundCommitment, fundManaged, blaize },
  commonRoute,
} = URL;

export const pingOldProfile = async (institution: string) => {
  try {
    const url = `\\institution-profiles\\${institution.formatString()}.html`;
    const response = await get(url, URL.basePEIURL);
    if (response.status !== 200) {
      return false;
    }
    return true;
  } catch (error) {
    return false;
  }
};

export const getHeadLines = async (institutionId: string) => {
  try {
    const url = `${relatedNews}?page=1&size=8&relatedToInstitution=${institutionId}&order=createdDate,desc`;
    const response = await retry(async () => await get(url), `${institutionId}/headlines`);
    const headlineRecords = response.data.records;
    const totalCount = response.data.pagination.total;
    const headLines = headlineRecords.sort((x: any, y: any) =>
      sortByDate(x, y, "createdDate", "dsc")
    );

    return { totalCount, headLines };
  } catch (ex) {
    return { totalCount: 0, headLines: [] };
  }
};

export const getContacts = async (institutionId: string) => {
  try {
    const url = `${commonRoute}${institutionId}${contactInfo}`;
    const response = await retry(async () => await get(url), `${institutionId}/contacts`);
    const officesRec = response.data.record.offices;
    const officesCount = officesRec.length;
    const officesHQ = officesRec.filter((x: any) => x.isHeadOffice);
    const contactsCount = response.data.record.people.length;

    const contacts = response.data.record.people
      .filter((x: any) => !x.isHeadOffice)
      .sort((x: any, y: any) => sortByAlphabetOrder(x, y, "givenName", ""))
      .slice(0, 4)
      .map((item: any) => {
        item.email = generateRandomChar();
        return item;
      });

    return { officesCount, officesHQ, contacts, contactsCount };
  } catch (ex) {
    return { officesCount: 0, officesHQ: [], contacts: [], contactsCount: 0 };
  }
};

export const getFundsManagedInMarket = async (institutionId: string, partnerStatus: string) => {
  try {
    const fundsManaged = getConditionalStatusWithPartnerStatus("funds-managed", partnerStatus);
    if (!fundsManaged) return { inMarketFundsCount: 0, inMarketFunds: [] };
    const url = `${commonRoute}${institutionId}${fundManaged}?fundsType=inMarket&page=${pageNumber}&size=${maxNumberOfRecords}`;
    const response = await retry(async () => await get(url), `${institutionId}/funds-managed`);
    const inMarketFundsCount = response.data.pagination.total;
    const arrayWithNoDatesRecords: Array<any> = [];
    const arrayWithDatesRecords: Array<any> = [];

    response.data.records.forEach((item: any, index: number) => {
      if (!item.openDateMonth || !item.openDateYear) {
        item.openDate = "-";
        arrayWithDatesRecords.push(item);
        return;
      }

      const date = new Date();
      date.setMonth(item.openDateMonth - 1);
      date.setFullYear(item.openDateYear);
      item.openDate = date;
      if (isNaN(item.openDate.getTime())) {
        return;
      } else {
        arrayWithDatesRecords.push(item);
      }
    });

    let inMarketFunds = arrayWithDatesRecords
      .sort((x: any, y: any) => sortByDate(x, y, "openDate", "dsc"))
      .slice(0, 3);

    inMarketFunds = [...inMarketFunds, ...arrayWithNoDatesRecords];
    inMarketFunds = inMarketFunds.slice(0, 3);
    const finalInMarketFunds = inMarketFunds.map(({ name, openDate, id }: any, index: number) => {
      const newItem: Array<FlexibleTableProp> = [
        { text: generateRandomChar(), icon: "lock" },
        { text: generateRandomChar(), icon: "lock" },
        {
          text:
            openDate === "-"
              ? "-"
              : `${getMonthName(openDate.getMonth())} ${openDate.getFullYear()}`,
        },
        { text: generateRandomChar(), icon: "lock" },
      ];

      if (index === 0) {
        newItem[0] = {
          text: name,
          link: isBrandToSkip
            ? null
            : `${URL.basePEIURL}/database/fund-profile/id/${id}/${name}/fund-overview/`,
        };
      }

      return newItem;
    });

    return { inMarketFundsCount, inMarketFunds: finalInMarketFunds };
  } catch (error) {
    return { inMarketFundsCount: 0, inMarketFunds: [] };
  }
};

export const getFundsManagedClosed = async (institutionId: string, partnerStatus: string) => {
  try {
    const fundsManaged = getConditionalStatusWithPartnerStatus("funds-managed", partnerStatus);
    if (!fundsManaged) return { closedFundsCount: 0, closedFunds: [] };

    const url = `${commonRoute}${institutionId}${fundManaged}?fundsType=closed&page=${pageNumber}&size=${maxNumberOfRecords}`;
    const response = await retry(
      async () => await get(url),
      `${institutionId}/fund-managed-closed`
    );
    const closedFundsCount = response.data.pagination.total;
    const arrayWithNoDatesRecords: Array<any> = [];
    const arrayWithDatesRecords: Array<any> = [];

    response.data.records.forEach((item: any, index: number) => {
      if (!item.closeDateYear || !item.closeDateMonth) {
        item.closeDate = "-";
        arrayWithNoDatesRecords.push(item);
        return;
      }

      const date = new Date();
      date.setMonth(item.closeDateMonth - 1);
      date.setFullYear(item.closeDateYear);
      item.closeDate = date;
      if (isNaN(item.closeDate.getTime())) {
        return;
      } else {
        arrayWithDatesRecords.push(item);
      }
    });

    let closedFunds = arrayWithDatesRecords
      .sort((x: any, y: any) => sortByDate(x, y, "closeDate", "dsc"))
      .slice(0, 3);

    closedFunds = [...closedFunds, ...arrayWithNoDatesRecords];
    closedFunds = closedFunds.slice(0, 3);
    const finalClosedFunds = closedFunds.map(({ name, closeDate, id }: any, index: number) => {
      const newItem: Array<FlexibleTableProp> = [
        { text: generateRandomChar(), icon: "lock" },
        { text: generateRandomChar(), icon: "lock" },
        {
          text:
            closeDate === "-"
              ? "-"
              : `${getMonthName(closeDate.getMonth())} ${closeDate.getFullYear()}`,
        },
        { text: generateRandomChar(), icon: "lock" },
      ];

      if (index === 0) {
        newItem[0] = {
          text: name,
          link: isBrandToSkip
            ? null
            : `${URL.basePEIURL}/database/fund-profile/id/${id}/${name}/fund-overview/`,
        };
      }

      return newItem;
    });

    return { closedFundsCount, closedFunds: finalClosedFunds };
  } catch (error) {
    return { closedFundsCount: 0, closedFunds: [] };
  }
};

export const getInvestmentAppetite = async (institutionId: string, partnerStatus: string) => {
  const overviewData = {
    description: { background: { __html: "No background data exists" } },
    email: "",
    website: "",
    hQ: {
      address: {
        city: {
          name: "",
        },
        country: {
          name: "",
        },
      },
    },
  };
  try {
    const checkInvestmentAppetite = getConditionalStatusWithPartnerStatus(
      "investment-appetite",
      partnerStatus
    );
    const investmentAppetite: investmentAppetiteType = {
      strategy: {} as investmentAppetiteItemType,
      region: {} as investmentAppetiteItemType,
      sector: {} as investmentAppetiteItemType,
    };

    const url = `${commonRoute}${institutionId}`;
    const response = await retry(
      async () => await get(url),
      `${institutionId}/investment-appetite`
    );
    const record = response.data.record;
    overviewData.description = record?.backgrounds[0]
      ? {
          background: {
            __html: record?.backgrounds[0]?.background
              ?.replaceAll("\r\n", "<br/>")
              ?.replaceAll("\n", "<br/>")
              ?.replaceAll("\t", "&emsp;"),
          },
        }
      : overviewData.description;
    overviewData.email = record.email ? record.email : overviewData.email;
    overviewData.website = record.website ? record.website : overviewData.website;
    const filterHQ = record.branches.filter((x: any) => x.isHeadOffice)[0];
    overviewData.hQ = filterHQ ? filterHQ : overviewData.hQ;
    const investmentRec = record.institutionFocuses;
    investmentRec.map((investment: any) => {
      if (investment.forPartnerStatus.name === "LP") {
        investmentAppetite.region = investment.regionFocus
          ? investment.regionFocus
              .sort((x: investmentAppetiteItemType, y: investmentAppetiteItemType) =>
                sortByAlphabetOrder(x, y, "name", "PE - ")
              )
              .shift()
          : {};
        investmentAppetite.strategy = investment.strategyFocus
          ? investment.strategyFocus
              .sort((x: investmentAppetiteItemType, y: investmentAppetiteItemType) =>
                sortByAlphabetOrder(x, y, "name", "PE - ")
              )
              .shift()
          : {};
        investmentAppetite.sector = investment.sectorFocus
          ? investment.sectorFocus
              .sort((x: investmentAppetiteItemType, y: investmentAppetiteItemType) =>
                sortByAlphabetOrder(x, y, "name", "PE - ")
              )
              .shift()
          : {};
      }
    });

    if (!checkInvestmentAppetite)
      return { overviewData, investmentAppetite: {}, hasApetiteRecord: false };
    const hasApetiteRecord =
      investmentAppetite.region || investmentAppetite.strategy || investmentAppetite.strategy;
    return {
      investmentAppetite,
      hasApetiteRecord: JSON.stringify(hasApetiteRecord) !== "{}",
      overviewData,
    };
  } catch (ex) {
    return { overviewData, investmentAppetite: {}, hasApetiteRecord: false };
  }
};

export const getFundCommitments = async (institutionId: string, partnerStatus: string) => {
  try {
    const checkFundCommitment = getConditionalStatusWithPartnerStatus(
      "fund-commitment",
      partnerStatus
    );
    if (!checkFundCommitment) return { fundCommitmentsCount: 0, fundCommitments: [] };
    const fundRecord: fundCommitmentType[] = [];
    const url = `${commonRoute}${institutionId}${fundCommitment}?page=1&size=${maxNumberOfRecords}`;
    const response = await retry(async () => await get(url), `${institutionId}/fund-commitments`);
    const fundCommitments = response.data.records;
    const fundCommitmentsCount = fundCommitments.length;
    //  first sorting with in infund itself with vintage year
    fundCommitments.map((x: any) =>
      x.inFund.sort(
        (a: any, b: any) => new Date(b.vintageYear).valueOf() - new Date(a.vintageYear).valueOf()
      )
    );
    //  secondly sorting with infund vintage year as compare to other records
    fundCommitments.sort(
      (a: any, b: any) =>
        new Date(b.inFund[0].vintageYear).valueOf() - new Date(a.inFund[0].vintageYear).valueOf()
    );
    fundCommitments.slice(0, 3).map((item: any, index: number) => {
      const fundLocal: fundCommitmentType = {} as fundCommitmentType;
      fundLocal.fund = index !== 0 ? generateRandomChar() : item.inFund[0].name;
      fundLocal.id = index === 0 && item.inFund[0].id;
      fundLocal.manager = generateRandomChar();
      fundLocal.commitmentDate = item.commitmentDate
        ? `${getMonthByName(item.commitmentDate)} ${new Date(item.commitmentDate).getFullYear()}`
        : "-";

      fundLocal.commitmentSize =
        index !== 0
          ? generateRandomChar()
          : !item.commitmentSize
          ? "-"
          : !item.commitmentSize.usdValue
          ? "-"
          : numberFormat(item.commitmentSize.usdValue);
      fundRecord.push(fundLocal);
    });
    return { fundCommitmentsCount, fundCommitments: fundRecord };
  } catch (ex) {
    return { fundCommitmentsCount: 0, fundCommitments: [] };
  }
};

let profileRecords: any = [];
let regions:any=[];
export const getProfilesByRegionAndPartnerStatus: any = async (partnerStatus: string, regionIndex = -1, page = 1, size = 500) => {
  try {
    if (regionIndex === -1) {
      profileRecords = [];
      regions = [];
      //get all the regions available
      const response = await retry(async () => await get("/regions"));
      regions = response.data.records;
      regionIndex = 0;
    }
    const url = `/institutions?partnerStatus=${partnerStatus}&page=${page}&size=${size}&locRegion=${regions[regionIndex]["id"]}`;
    const response = await retry(async () => await get(url), `${partnerStatus}/profiles`);
    const pageCount = response.data.pagination.pageCount;
    profileRecords = [...profileRecords, ...response.data.records];

    if(page === pageCount){
      regionIndex++;
      page = 0;
    }
    if (regionIndex===regions.length) {

      const profileRec = profileRecords.map((pr: any) => {
        return {
          Institution: pr.name,
          ID: pr.id,
          PartnerStatus: partnerStatus,
        } as instituteProps;
      });

      return await readAndUpdateJsonFile(profileRec, partnerStatus);

    } else {
      return getProfilesByRegionAndPartnerStatus(partnerStatus,regionIndex, page + 1, size);
    }
  } catch (ex: any) {
    console.log(
      `getProfilesByRegionAndPartnerStatus: Server responded with status code ${ex.response?.status} while fetching ${partnerStatus} profiles`
    );
    return false;
  }
}

export const getProfiles: any = async (partnerStatus: string, page = 1, size = 500) => {
  try {
    if (page === 1) {
      profileRecords = [];
    }
    const url = `/institutions?partnerStatus=${partnerStatus}&page=${page}&size=${size}`;
    const response = await retry(async () => await get(url), `${partnerStatus}/profiles`);
    const pageCount = response.data.pagination.pageCount;
    profileRecords = [...profileRecords, ...response.data.records];
    if (page === pageCount) {
      const profileRec = profileRecords.map((pr: any) => {
        return {
          Institution: pr.name,
          ID: pr.id,
          PartnerStatus: partnerStatus,
        } as instituteProps;
      });
      return await readAndUpdateJsonFile(profileRec, partnerStatus);
    } else {
      return getProfiles(partnerStatus, page + 1, size);
    }
  } catch (ex: any) {
    console.log(
      `Server responded with status code ${ex.response?.status} while fetching ${partnerStatus} profiles`
    );
    return false;
  }
};

const readAndUpdateJsonFile = async (fetchedData: instituteProps[], partnerStatus: string) => {
  return new Promise(async (resolve, reject) => {
    await fs.readFile(jsonFilePath, "utf8", async (err: any, data: any) => {
      if (err) {
        console.error(err);
        return;
      }

      const jsonFileData = JSON.parse(data);
      jsonFileData.Profiles.length > 0 &&
        jsonFileData.Profiles.map((jdata: instituteProps) => {
          const foundRec = fetchedData.find((x: instituteProps) => x.ID === jdata.ID);
          if (foundRec) {
            if (foundRec.PartnerStatus !== jdata.PartnerStatus) jdata.PartnerStatus = "LP/GP";
            foundRec.Duplicated = true;
          }
        });
      const filteredArray = fetchedData.filter((obj: instituteProps) => !(obj.Duplicated === true));
      const updatedProfileRecords = [...jsonFileData["Profiles"], ...filteredArray];
      const profileStructure = {
        Profiles: updatedProfileRecords,
      };
      // Convert the updated JSON object back to a string
      const updatedData = JSON.stringify(profileStructure, null, 2);

      // Write the updated data back to the file
      return await fs.writeFile(jsonFilePath, updatedData, "utf8", (err: any) => {
        if (err) {
          console.log(
            `There is something went wrong while updating ${partnerStatus} profiles data`
          );
          console.error(err);
          reject(false);
        }
        console.log(
          `Total ${fetchedData.length} of ${partnerStatus} profiles data updated successfully`
        );
        resolve(true);
      });
    });
  });
};
